/**
 * Location calendar / timeslot background colors for Track Appointment.
 *
 * ## How coloring works
 *
 * - **Legacy** (`REACT_APP_FEATURE_PMS_BOOKING_ENABLED=false`): Firebase availability stores
 *   `selectedRoom` (1–4). Colors come from `ROOM_COLOR_MAPPING[key]`.
 * - **PMS booking** (`REACT_APP_FEATURE_PMS_BOOKING_ENABLED=true`): Availability API returns
 *   `locationId` (PMS UUID) on each slot. We map `locationId` → `key` via `ROOM_LIST`, then
 *   use the same palette.
 *
 * Unmapped slots (no `selectedRoom`, unknown `locationId`, or `key` not in palette) render as
 * yellow fallback (`no-room-available-background` in TrackAppointment/index.css).
 *
 * ## Steps to add a background color for a new PMS location
 *
 * 1. **Create the location in PMS** and ensure availability rules attach that location to slots
 *    (slots without `locationId` in `/pms/admin/availability` cannot be colored).
 * 2. **Get the location UUID** — e.g. run `/pms/admin/preload` and check Firestore `pms_locations`,
 *    or read `locationId` from the availability API response in DevTools.
 * 3. **Pick a palette key (1–4)** — each key maps to a fixed color in
 *    `src/components/TrackAppointment/constant.ts` (`ROOM_COLOR_MAPPING`). Reuse a key already
 *    used by another room if you are out of distinct colors (max 4).
 * 4. **Bind UUID to key** in `src/configurableConst.ts` under the hospital matching
 *    `REACT_APP_HOSPITAL_NAME` for this deployment:
 *    ```ts
 *    MACKAY_EYE: [
 *      { key: 4, label: "Room 4: Own Device", locationId: "<pms-location-uuid>" },
 *    ],
 *    ```
 *    `label` is for Schedule / admin display only; coloring uses `key` + `locationId`.
 * 5. **Restart the dev server** (`yarn dev`) so env and `configurableConst` changes load.
 * 6. **Verify** in Network → `/pms/admin/availability` that slots include your `locationId`,
 *    then confirm calendar / timeslot cells use the expected color (not yellow).
 *
 * To add a **fifth distinct color**, extend `ROOM_COLOR_MAPPING`, add a CSS class in
 * `TrackAppointment/index.css`, and update `getRoomColor` below.
 */
import { ROOM_LIST } from "../configurableConst";
import { ROOM_COLOR_MAPPING } from "../components/TrackAppointment/constant";

/** Shape of a ROOM_LIST entry. Declared here as the only consumer of it. */
type RoomListEntry = {
  key: number;
  label: string;
  /** PMS location UUID — set when using pms booking availability for calendar colors */
  locationId?: string;
};

export type TimeslotColorIdentity = {
  selectedRoom?: number;
  locationId?: string;
};

export type RoomColorStyle = {
  color: string;
  className: string;
};

// configurableConst is plain JS, so TypeScript infers a literal object type for
// ROOM_LIST with no index signature. Annotate once so hospital-name lookup is typed.
const ROOM_LIST_BY_HOSPITAL: Record<string, RoomListEntry[]> = ROOM_LIST;

const getHospitalRoomList = (): RoomListEntry[] => {
  const hospitalName = process.env.REACT_APP_HOSPITAL_NAME;
  if (!hospitalName) {
    return [];
  }
  return ROOM_LIST_BY_HOSPITAL[hospitalName] ?? [];
};

/**
 * Palette key (1–4) for a timeslot.
 * Legacy: `selectedRoom`. PMS: `locationId` matched against `ROOM_LIST[REACT_APP_HOSPITAL_NAME]`.
 */
export const resolveColorKey = (timeslot: TimeslotColorIdentity): number | undefined => {
  if (Number.isInteger(timeslot.selectedRoom)) {
    return timeslot.selectedRoom;
  }

  if (!timeslot.locationId) {
    return undefined;
  }

  const normalizedId = String(timeslot.locationId).trim().toLowerCase();
  const room = getHospitalRoomList().find(
    (entry) => entry.locationId && String(entry.locationId).trim().toLowerCase() === normalizedId
  );

  return room?.key;
};

/** Returns hex + CSS class for a palette key; undefined → yellow fallback in UI. */
export const getRoomColor = (colorKey?: number): RoomColorStyle | undefined => {
  switch (colorKey) {
    case 1:
      return ROOM_COLOR_MAPPING[1];
    case 2:
      return ROOM_COLOR_MAPPING[2];
    case 3:
      return ROOM_COLOR_MAPPING[3];
    case 4:
      return ROOM_COLOR_MAPPING[4];
    default:
      return undefined;
  }
};

export const collectColorKeysFromTimeslots = (timeslots: TimeslotColorIdentity[] = []): number[] => {
  const keys: number[] = [];
  timeslots.forEach((timeslot) => {
    const key = resolveColorKey(timeslot);
    if (Number.isInteger(key) && getRoomColor(key)) {
      keys.push(key);
    }
  });
  return Array.from(new Set(keys));
};
